import React, { useState, useEffect } from 'react';
import { TrashIcon, ChevronUpIcon, ChevronDownIcon, PencilIcon } from '@heroicons/react/24/solid';
import {
  DocumentTextIcon,
  PlusIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { IProjectScript } from '@packages/global';
import { useToast } from '../../stores/toastStore';
import { useTranslation } from 'react-i18next';

// Custom Save icon (lucide save)
function SaveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
      <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7" />
      <path d="M7 3v4a1 1 0 0 0 1 1h7" />
    </svg>
  );
}

interface ScriptEditorProps {
  projectId: string;
  chapters: IProjectScript[];
  loading?: boolean;
  selectedId?: string | null;
  onAddChapter: () => void;
  onEditChapter: (chapter: IProjectScript) => void;
  onSelectChapter: (chapter: IProjectScript) => void;
  onSaveChapter: (chapter: IProjectScript) => void;
  onDeleteChapter?: (chapter: IProjectScript) => void;
  onReorderChapters?: (chapters: IProjectScript[]) => void;
  onCreateEpisode?: (chapter: IProjectScript) => void;
  isCreatingEpisode?: boolean;
}

export default function ScriptEditor({
  projectId,
  chapters,
  loading,
  selectedId,
  onAddChapter,
  onEditChapter,
  onSelectChapter,
  onSaveChapter,
  onDeleteChapter,
  onReorderChapters,
  onCreateEpisode,
  isCreatingEpisode
}: ScriptEditorProps) {
  const toast = useToast();
  const { t } = useTranslation(['project', 'script', 'common']);
  const [selectedChapterId, setSelectedChapterId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastAppliedContent, setLastAppliedContent] = useState<string | null>(null);

  useEffect(() => {
    if (selectedId) {
      const chapter = chapters.find(c => c.id === selectedId);
      if (chapter) {
        setSelectedChapterId(selectedId);
        setEditingContent(chapter.content);
        setLastAppliedContent(chapter.content);
      }
    }
  }, [selectedId]);

  useEffect(() => {
    if (selectedChapterId) {
      const chapter = chapters.find(c => c.id === selectedChapterId);
      if (chapter && chapter.content !== lastAppliedContent) {
        setEditingContent(chapter.content);
        setLastAppliedContent(chapter.content);
      }
    }
  }, [chapters, selectedChapterId, lastAppliedContent]);

  useEffect(() => {
    if (chapters.length > 0 && !selectedChapterId && !selectedId) {
      const firstChapter = chapters[0];
      setSelectedChapterId(firstChapter.id);
      setEditingContent(firstChapter.content);
      setLastAppliedContent(firstChapter.content);
      onSelectChapter(firstChapter);
    }
  }, [chapters, selectedChapterId, selectedId]);

  const selectedChapter = chapters.find(c => c.id === selectedChapterId);

  const handleSelectChapter = (chapter: IProjectScript) => {
    setSelectedChapterId(chapter.id);
    setEditingContent(chapter.content);
    setLastAppliedContent(chapter.content);
    onSelectChapter(chapter);
  };

  const handleSave = async () => {
    if (selectedChapter) {
      setSaving(true);
      try {
        await onSaveChapter({ ...selectedChapter, content: editingContent });
        toast.success(t('project:script.chapterSaved'));
      } catch (error) {
        toast.error(t('project:script.saveFailed'));
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDelete = (e: React.MouseEvent, chapter: IProjectScript) => {
    e.stopPropagation();
    if (onDeleteChapter) onDeleteChapter(chapter);
  };

  const handleEdit = (e: React.MouseEvent, chapter: IProjectScript) => {
    e.stopPropagation();
    onEditChapter(chapter);
  };

  const handleMoveUp = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (index === 0 || !onReorderChapters) return;
    const newChapters = [...chapters];
    [newChapters[index - 1], newChapters[index]] = [newChapters[index], newChapters[index - 1]];
    onReorderChapters(newChapters.map((ch, idx) => ({ ...ch, chapterNumber: idx + 1, sortOrder: idx + 1 })));
  };

  const handleMoveDown = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (index === chapters.length - 1 || !onReorderChapters) return;
    const newChapters = [...chapters];
    [newChapters[index], newChapters[index + 1]] = [newChapters[index + 1], newChapters[index]];
    onReorderChapters(newChapters.map((ch, idx) => ({ ...ch, chapterNumber: idx + 1, sortOrder: idx + 1 })));
  };

  if (loading) {
    return (
      <div className="flex-1 flex gap-6 min-h-0">
        <div className="w-80 flex flex-col gap-4 flex-shrink-0">
          <div className="h-5 w-20 bg-border-subtle animate-pulse rounded" />
          <div className="flex flex-col gap-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-11 bg-bg-elevated animate-pulse rounded-lg" />
            ))}
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-4 min-h-0">
          <div className="h-7 w-48 bg-border-subtle animate-pulse rounded flex-shrink-0" />
          <div className="flex-1 bg-bg-surface rounded-xl animate-pulse min-h-0" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex gap-6 min-h-0 h-full">
      {/* Left Panel - Chapter List */}
      <div className="w-80 flex flex-col gap-4 flex-shrink-0 overflow-y-auto">
        <h3 className="text-sm font-semibold text-gray-200 flex-shrink-0">{t('project:script.chapterList')}</h3>

        <div className="flex flex-col gap-1">
          {chapters.map((chapter, index) => {
            const isActive = chapter.id === selectedChapterId;
            return (
              <div
                key={chapter.id}
                className={`flex items-center gap-2 px-3 py-3 rounded-lg text-left transition-colors cursor-pointer group ${isActive ? 'bg-bg-elevated' : 'hover:bg-bg-surface'
                  }`}
                onClick={() => handleSelectChapter(chapter)}
              >
                {onReorderChapters && (
                  <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => handleMoveUp(e, index)}
                      disabled={index === 0}
                      className="p-0.5 text-gray-400 hover:text-gray-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                      title={t('project:script.moveUp')}
                    >
                      <ChevronUpIcon className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => handleMoveDown(e, index)}
                      disabled={index === chapters.length - 1}
                      className="p-0.5 text-gray-400 hover:text-gray-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                      title={t('project:script.moveDown')}
                    >
                      <ChevronDownIcon className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded flex-shrink-0 ${isActive ? 'bg-primary/20 text-primary' : 'bg-border-subtle text-gray-400'
                  }`}>
                  {chapter.chapterNumber}
                </span>
                <span className={`flex-1 truncate text-sm ${isActive ? 'text-gray-50' : 'text-gray-300'}`}>
                  {chapter.title}
                </span>
                {chapter.generatedEpisodeId && (
                  <span className="px-1.5 py-0.5 text-[10px] bg-success/20 text-success rounded flex-shrink-0">
                    {t('project:script.generated')}
                  </span>
                )}
                <button
                  onClick={(e) => handleEdit(e, chapter)}
                  className="p-1 text-gray-400 hover:text-primary opacity-0 group-hover:opacity-100 transition-all flex-shrink-0 cursor-pointer"
                  title={t('project:script.editTitle')}
                >
                  <PencilIcon className="w-3.5 h-3.5" />
                </button>
                {onDeleteChapter && (
                  <button
                    onClick={(e) => handleDelete(e, chapter)}
                    className="p-1 text-gray-400 hover:text-error opacity-0 group-hover:opacity-100 transition-all flex-shrink-0 cursor-pointer"
                    title={t('project:chapter.delete')}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}

          <button
            onClick={onAddChapter}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-border-default text-gray-400 hover:text-gray-300 hover:bg-bg-elevated transition-colors cursor-pointer"
          >
            <PlusIcon className="w-4 h-4" />
            <span>{t('project:script.addChapter')}</span>
          </button>
        </div>
      </div>

      {/* Right Panel - Editor */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {selectedChapter ? (
          <>
            <div className="flex items-center justify-between flex-shrink-0 mb-4">
              <h2 className="text-lg font-semibold text-gray-50">
                {selectedChapter.title}
              </h2>
              {onCreateEpisode && (
                <button
                  onClick={() => onCreateEpisode(selectedChapter)}
                  disabled={isCreatingEpisode}
                  className="flex items-center gap-2 px-4 py-2 border border-primary/50 hover:border-primary text-primary hover:bg-primary/10 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  <SparklesIcon className="w-4 h-4" />
                  {t('project:script.createEpisodeVideo')}
                </button>
              )}
            </div>

            <div className="flex-1 bg-bg-surface rounded-xl border border-border-subtle flex flex-col min-h-0 overflow-hidden">
              <textarea
                value={editingContent}
                onChange={(e) => setEditingContent(e.target.value)}
                placeholder={t('project:script.scriptPlaceholder')}
                className="flex-1 w-full p-5 bg-transparent text-gray-200 placeholder-gray-400 resize-none focus:outline-none text-sm leading-[1.8] overflow-y-auto"
              />
              <div className="flex justify-center py-4 border-t border-border-subtle flex-shrink-0">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <SaveIcon className="w-4 h-4" />
                  {saving ? t('script:action.saving') : t('common:save')}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <DocumentTextIcon className="w-12 h-12 mb-3" />
            <p>{t('project:script.noChapter')}</p>
            <p className="text-sm mt-1">{t('project:script.noChapterHint')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
